import React, { useState, useEffect, useRef } from 'react'
import { useIntl } from 'react-intl'
import { useForm } from 'react-hook-form'
import {
  AlertBox,
  Icons,
  DynamicTag,
  Button,
  cleanPrefixPhone,
  getPrefixPhone,
  removeInitialZero,
  PageActionPlaceHolder,
  Spinner
} from 'msp-components'
import {
  useCountryData,
  useCustomer,
  ICustomerAddress,
  validatePostalCode
} from 'msp-integrations'
import AddressCreateEditForm from '@/components/molecules/AddressCreateEditForm/AddressCreateEditForm'
import AddressDisplay from '@/components/atoms/AddressDisplay/AddressDisplay'
import ActionCard from '@/components/molecules/ActionCard/ActionCard'
import { ActionCardOtherActionProps } from '@/components/molecules/ActionCard/ActionCard.types'
import translations from './AccountAddresses.constants'
import * as S from './AccountAddresses.styles'
import * as T from './AccountAddresses.types'

const AccountAddressesNoSavedAddress = ({
  ctaActionCB
}: T.AccountAddressesNoSavedAddressProps) => {
  const { formatMessage } = useIntl()

  return (
    <PageActionPlaceHolder
      iconName='Building'
      heading={formatMessage(translations.placeholderHeading)}
      text={formatMessage(translations.placeholderText)}
      buttonText={formatMessage(translations.addNewAddressMessage)}
      onButtonClick={ctaActionCB}
    />
  )
}

const AccountAddresses = ({ scrollTop }: T.AccountAddressesProps) => {
  const alertBoxRef = useRef<AlertBox>()
  const { formatMessage } = useIntl()
  const i18n = useCountryData()
  const {
    addresses = [],
    addSavedAddress,
    updateSavedAddress,
    removeSavedAddress
  } = useCustomer()
  const [hasSavedAddresses, setHasSavedAddresses] = useState(false)
  const [isRemovingAddress, setIsRemovingAddress] = useState(false)
  const [isNewDefaultAddress, setIsNewDefaultAddress] = useState(false)
  const [isEditingAddress, setIsEditingAddress] = useState(false)
  const [isSettingAddressByDefault, setIsSettingAddressByDefault] =
    useState(false)
  const [addressToRemove, setAddressToRemove] =
    useState<ICustomerAddress | null>(null)
  const [selectedAddressId, setSelectedAddressId] = useState<
    string | undefined
  >(undefined)

  useEffect(() => {
    if (addresses?.length > 0) {
      setHasSavedAddresses(true)
    }
  }, [addresses])

  const form = useForm({
    mode: 'onChange',
    shouldUnregister: false
  })

  const confirmAddressRemove = async (addressId: string) => {
    try {
      alertBoxRef?.current?.closeAlert()
      setIsRemovingAddress(true)
      await removeSavedAddress(addressId)
      setIsRemovingAddress(false)
      setAddressToRemove(null)
    } catch (error) {
      form.setError('global', { type: 'manual', message: error.message })
    }
  }

  const onRemoveSavedAddress = async (address: ICustomerAddress) => {
    setAddressToRemove(address)
    alertBoxRef?.current?.showAlert()
    return Promise.resolve()
  }

  const cancelAddressRemove = () => {
    setAddressToRemove(null)
    setIsRemovingAddress(false)
    alertBoxRef?.current?.closeAlert()
  }

  const setAsDefaultAddress = async (address: ICustomerAddress) => {
    try {
      if (address?.addressId) {
        setIsSettingAddressByDefault(true)
        await updateSavedAddress({ ...address, preferred: true })
        setIsSettingAddressByDefault(false)
        scrollTop && scrollTop()
        setIsNewDefaultAddress(true)
        setTimeout(() => {
          setIsNewDefaultAddress(false)
        }, 750)
      }
    } catch (error) {
      form.setError('global', { type: 'manual', message: error.message })
    }
  }

  // Opens/closes the 'add address' form. Notice that when toggling either state,
  // we reset the form so as to remove any address selection.
  const toggleAddressEdit = (address = undefined) => {
    if (address?.addressId) {
      setIsEditingAddress(true)
      setSelectedAddressId(address.addressId)
      address.phonePrefix = getPrefixPhone(address.phone)
      address.phone = cleanPrefixPhone(address.phone)
      form?.reset({ ...address })
    } else {
      setSelectedAddressId(undefined)
      setIsEditingAddress(!isEditingAddress)
      form?.reset({ addressId: '' })
    }

    form?.trigger()
  }

  const setAsDefaultAddressAction: ActionCardOtherActionProps = {
    label: {
      defaultMessage: 'Set as default',
      id: 'use_address_fields.label.preferred'
    },
    className: S.AddressSetAsDefaultActionButton,
    actionHandler: () => null
  }

  // Updates the selected customer address if we've an address selected
  // else saves a new customer address
  const submitForm = async (address: ICustomerAddress) => {
    try {
      const isPostalCodeValid = validatePostalCode(address, i18n)
      if (!isPostalCodeValid) {
        form.setError('postalCode', {
          type: 'manual',
          message: formatMessage({
            defaultMessage: 'Please enter valid postal code.',
            id: 'msp.address_form.error.please_enter_valid_postal'
          })
        })
        return
      }
      address.phone =
        address.phone &&
        address?.phonePrefix?.concat(
          removeInitialZero(cleanPrefixPhone(address.phone))
        )
      delete address?.phonePrefix
      form.clearErrors()

      if (selectedAddressId) {
        await updateSavedAddress({ ...address, addressId: selectedAddressId })
      } else {
        delete address?.addressId
        await addSavedAddress(address)
      }

      toggleAddressEdit()
      scrollTop && scrollTop()
    } catch (error) {
      form.setError('global', { type: 'manual', message: error.message })
    }
  }

  return (
    <>
      <DynamicTag
        componentTagName='Headline'
        tag='h2'
        tagStyle='h6'
        className={S.AddressesHeadline}
      >
        {formatMessage(translations.myAddresses)}
      </DynamicTag>
      <form onSubmit={form.handleSubmit(submitForm)} autoComplete={'off'}>
        <div className={S.AddressesListWrapper}>
          {addresses.length > 0 &&
            addresses.map((address, index) => (
              <div
                className={S.AddressItemWrapper}
                key={`address-${address.addressId}`}
              >
                <div
                  className={`${S.AddressItemContentWrapper}
                    ${
                      index === 0 &&
                      isNewDefaultAddress &&
                      S.AddressItemNewDefaultAddressWrapper
                    }
                    ${
                      isEditingAddress &&
                      address.addressId === selectedAddressId &&
                      S.AddressItemContentEditModeWrapper
                    } ${
                    address.c_isPackstation &&
                    !isEditingAddress &&
                    S.AddressItemContentPackstationWrapper
                  }`}
                >
                  <ActionCard
                    noSpinner
                    otherActionsPostion='before'
                    otherActions={
                      !address.preferred
                        ? [
                            {
                              ...setAsDefaultAddressAction,
                              actionHandler: () => setAsDefaultAddress(address)
                            }
                          ]
                        : []
                    }
                    onRemove={() => onRemoveSavedAddress(address)}
                    onEdit={() => toggleAddressEdit(address)}
                  >
                    {address.preferred && (
                      <div className={S.AddressPreferredAddressLabel}>
                        {formatMessage(translations.preferredAddress)}
                      </div>
                    )}
                    <AddressDisplay address={address} />
                  </ActionCard>
                </div>
                {isEditingAddress &&
                  address.addressId === selectedAddressId && (
                    <div className={S.AddressEditModeWrapper}>
                      <AddressCreateEditForm
                        title={formatMessage(translations.editAddress)}
                        hasSavedAddresses={hasSavedAddresses}
                        toggleAddressEdit={toggleAddressEdit}
                        form={form}
                        saveButtonLabel={translations.saveChanges}
                        isEditingMode={isEditingAddress}
                        isPackstationAddress={address.c_isPackstation}
                        isLocker={address?.c_isLocker}
                      />
                    </div>
                  )}
              </div>
            ))}

          {addresses.length === 0 && (
            <AccountAddressesNoSavedAddress ctaActionCB={toggleAddressEdit} />
          )}
        </div>

        {addresses.length > 0 && (
          <Button
            className={S.AddressAddNewButton}
            onClick={toggleAddressEdit}
            isDisabled={isEditingAddress || form.formState.isSubmitting}
            isLoading={form.formState.isSubmitting}
            isSubmit
          >
            <div className='flex items-center'>
              <span className='mr-1 h-[18px]'>
                <Icons iconName='Plus' size='lg' />
              </span>
              <span className='leading-18 h-[18px]'>
                {formatMessage(translations.addNewAddressMessage)}
              </span>
            </div>
          </Button>
        )}
        {isEditingAddress && !selectedAddressId && (
          <AddressCreateEditForm
            title={formatMessage(translations.addNewAddressMessage)}
            toggleAddressEdit={toggleAddressEdit}
            hideSubmitButton={false}
            form={form}
            hasSavedAddresses={true}
            saveButtonLabel={translations.saveChanges}
          />
        )}
      </form>
      {(isRemovingAddress || isSettingAddressByDefault) && (
        <Spinner fullScreen />
      )}
      <AlertBox
        ref={alertBoxRef}
        type='standard'
        headlineColor='text-black'
        title={formatMessage(translations.addressRemoveTitle)}
        closeButtonIcon={<Icons iconName='Close' size='base' />}
      >
        <div className={S.AddressRemoveDescription}>
          {formatMessage(translations.addressRemoveDescription)}
        </div>

        {addressToRemove && (
          <AddressDisplay className='text-black' address={addressToRemove} />
        )}

        <div className={S.AddressRemoveNote}>
          {formatMessage(translations.addressRemoveNote)}
        </div>

        <div className={S.AddressConfirmBoxButtonsWrapper}>
          <Button
            type={'secondary'}
            variant='outline'
            className='mx-2'
            onClick={cancelAddressRemove}
          >
            {formatMessage(translations.addressRemoveCancel)}
          </Button>
          <Button
            type='secondary'
            onClick={() => confirmAddressRemove(addressToRemove.addressId)}
          >
            {formatMessage(translations.addressRemoveConfirm)}
          </Button>
        </div>
      </AlertBox>
    </>
  )
}

export default AccountAddresses
